export type QuickcamCoinLocationRestriction =
  | 'ALLOWED'
  | 'JURISDICTION_RESTRICTED'
  | 'LOCATION_INCOMPLETE';

export interface QuickcamCoinLocationDecision {
  isAllowed: boolean;
  countryCode?: string;
  stateName?: string;
  restriction: QuickcamCoinLocationRestriction;
}

/** Same blocklist as application-backend country-code.enum.ts */
export const BLOCKED_COUNTRIES = [
  'IR',
  'KP',
  'SY',
  'CU',
  'RU',
  'KY',
] as const;

const UNITED_STATES_CODES = new Set(['US', 'USA']);
const CALIFORNIA_VALUES = new Set(['CA', 'CALIFORNIA']);

function normalize(value: string | null | undefined): string | undefined {
  const normalized = value?.trim().toUpperCase();
  return normalized || undefined;
}

export function isCountryBlockedForPurchase(
  countryCode: string | null | undefined,
): boolean {
  if (!countryCode) {
    return false;
  }
  const normalized = countryCode.trim().toUpperCase();
  return (BLOCKED_COUNTRIES as readonly string[]).includes(normalized);
}

/**
 * Profile-only purchase policy. IP geo is intentionally absent.
 */
export function evaluateQuickcamCoinLocation(
  countryCode: string | null | undefined,
  stateName: string | null | undefined,
): QuickcamCoinLocationDecision {
  const normalizedCountry = normalize(countryCode);
  const normalizedState = normalize(stateName);

  if (!normalizedCountry) {
    return {
      isAllowed: false,
      restriction: 'LOCATION_INCOMPLETE',
    };
  }

  if (isCountryBlockedForPurchase(normalizedCountry)) {
    return {
      isAllowed: false,
      countryCode: normalizedCountry,
      stateName: normalizedState,
      restriction: 'JURISDICTION_RESTRICTED',
    };
  }

  if (UNITED_STATES_CODES.has(normalizedCountry)) {
    if (!normalizedState) {
      return {
        isAllowed: false,
        countryCode: normalizedCountry,
        restriction: 'LOCATION_INCOMPLETE',
      };
    }

    if (CALIFORNIA_VALUES.has(normalizedState)) {
      return {
        isAllowed: false,
        countryCode: normalizedCountry,
        stateName: normalizedState,
        restriction: 'JURISDICTION_RESTRICTED',
      };
    }
  }

  return {
    isAllowed: true,
    countryCode: normalizedCountry,
    stateName: normalizedState,
    restriction: 'ALLOWED',
  };
}
